import React from "react";
import { useTranslation } from "react-i18next";
import analyticsService from "../../services/analyticsService";
import { buildSupportWhatsAppUrl } from "../../constants/support";
import { numberToLetter } from "../../utils/rateUtils";
import Button from "../ui/Button";
import Icon from "../ui/Icon";
import SupportCallLink from "../ui/SupportCallLink";

// Retour au client après « Débloquer le câble » (QCT-670) : ordre envoyé, câble
// retiré, ou câble toujours bloqué avec de quoi réessayer ou joindre le support.
export default function CableUnlockStatus({
  status,
  station,
  connector,
  connectorNumber,
  transactionId,
  onRetry,
  onReportStuck,
}) {
  const { t } = useTranslation();

  if (status === "sent") {
    return (
      <div className="mt-4 rounded-2xl bg-qv-brand-50 p-4 md:mt-7" role="status">
        <div className="flex gap-3 items-start">
          <span
            className="mt-0.5 h-[18px] w-[18px] shrink-0 rounded-full border-2 border-qv-brand-100 border-t-qv-brand animate-spin"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm leading-5 font-bold">{t("startCharging.unlock.sentTitle")}</p>
            <p className="mt-0.5 text-[13px] leading-[19px] font-medium text-qv-muted">
              {t("startCharging.unlock.sentText")}
            </p>
          </div>
        </div>
        {onReportStuck && (
          <button
            type="button"
            onClick={onReportStuck}
            className="mt-3.5 w-full text-center text-sm font-semibold text-qv-brand hover:underline"
          >
            {t("startCharging.unlock.stillLockedTitle")}
          </button>
        )}
      </div>
    );
  }

  if (status === "released") {
    return (
      <div className="mt-4 rounded-2xl bg-qv-green-50 p-4 flex gap-3 items-start md:mt-7" role="status">
        <Icon name="check-circle-bold" size={22} className="text-qv-green" />
        <div>
          <p className="text-sm leading-5 font-bold text-qv-green-900">{t("startCharging.unlock.releasedTitle")}</p>
          <p className="mt-0.5 text-[13px] leading-[19px] font-medium text-qv-muted">
            {t("startCharging.unlock.releasedText")}
          </p>
        </div>
      </div>
    );
  }

  if (status === "stillLocked") {
    const transaction = transactionId || undefined;
    const analyticsParams = { station_id: station, connector_id: connector, context: "cable_unlock" };
    const connectorName = connectorNumber ? t("connector", { number: numberToLetter(connectorNumber) }) : connector;

    return (
      <div className="mt-4 rounded-2xl bg-qv-amber-50 p-4 md:mt-7" role="alert">
        <div className="flex gap-3 items-start">
          <Icon name="danger-triangle-bold" size={22} className="text-qv-amber" />
          <div>
            <p className="text-sm leading-5 font-bold">{t("startCharging.unlock.stillLockedTitle")}</p>
            <p className="mt-0.5 text-[13px] leading-[19px] font-medium text-qv-muted">
              {t("startCharging.unlock.stillLockedText")}
            </p>
          </div>
        </div>
        <div className="ml-[34px] mt-3 flex flex-col gap-1 text-[13px] font-semibold">
          <span>{t("startCharging.unlock.stationLine", { station: station || "-", connector: connectorName || "-" })}</span>
          {transaction && <span>{t("startCharging.unlock.transactionLine", { transaction })}</span>}
        </div>
        <div className="mt-4 flex flex-col gap-2.5">
          <Button icon="lock-unlocked-bold" onClick={onRetry}>
            {t("startCharging.unlock.retry")}
          </Button>
          <Button
            variant="whatsapp"
            icon="whatsapp"
            href={buildSupportWhatsAppUrl(t, { station, connector, transaction })}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => analyticsService.pushEvent("support_whatsapp_clicked", analyticsParams)}
          >
            {t("support.whatsapp")}
          </Button>
          <SupportCallLink analyticsParams={analyticsParams} />
        </div>
      </div>
    );
  }

  return null;
}
